import { useState } from 'react'

import { EllipsisVertical } from 'lucide-react'

import { Button } from '~/components/component-library'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { TransactionDropdownMenuItems } from '~/components/smart-accounts/TransactionDropdownMenuItems'
import { TransferDropdownMenuItems } from '~/components/smart-accounts/TransferDropdownMenuItems'
import { useTransactionProperties } from '~/hooks/useTransactions'
import { OrgAssetTransfer, OrgTransaction } from '~/types'
import { cn } from '~/utils/tailwind-utils'

export const TransactionFeedActions = ({
  transaction,
  isDropdownOpen,
  setIsDropdownOpen,
}: {
  transaction: OrgTransaction | OrgAssetTransfer
  isDropdownOpen: boolean
  setIsDropdownOpen: (isOpen: boolean) => void
}) => {
  const { isSplitsTransaction } = transaction

  return (
    <div className="flex shrink-0 items-center">
      {isSplitsTransaction && (
        <TransactionActionButton transaction={transaction} />
      )}
      <TransactionDropdownMenu
        transaction={isSplitsTransaction ? transaction : undefined}
        transfer={isSplitsTransaction ? undefined : transaction}
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={setIsDropdownOpen}
      />
    </div>
  )
}

const TransactionActionButton = ({
  transaction,
}: {
  transaction: OrgTransaction
}) => {
  const {
    canSign,
    canSubmit,
    canExecute,
    isSigning,
    hasSigned,
    isConfirmed,
    isSubmitting,
    isConfirming,
  } = useTransactionProperties(transaction)

  const showButton = !isConfirmed
  const canReview = (canSign && !hasSigned) || canSubmit || canExecute
  const isLoading = isSubmitting || isConfirming || isSigning

  if (!showButton) return null

  return (
    <div className="pl-6">
      <Button
        variant={canReview ? 'primary' : 'secondary'}
        // empty because the entire feed item is a trigger to open the txn dialog
        onClick={() => {}}
        isLoading={isLoading}
      >
        {canReview ? 'Review' : 'View'}
      </Button>
    </div>
  )
}

const TransactionDropdownMenu = ({
  transaction,
  transfer,
  isDropdownOpen,
  setIsDropdownOpen,
}: {
  transaction: OrgTransaction | undefined
  transfer: OrgAssetTransfer | undefined
  isDropdownOpen: boolean
  setIsDropdownOpen: (isOpen: boolean) => void
}) => {
  const [hasInteracted, setHasInteracted] = useState(false)

  if (!hasInteracted) {
    return (
      <div
        className={cn('lg:invisible lg:group-hover:visible')}
        onClick={(e) => {
          e.stopPropagation()
          setHasInteracted(true)
          setIsDropdownOpen(true)
        }}
      >
        <button type="button">
          <EllipsisVertical className="my-2 ml-3 mr-1 h-4 w-4 cursor-pointer" />
        </button>
      </div>
    )
  }

  return (
    <div
      className={cn('lg:invisible lg:group-hover:visible', {
        'lg:visible': isDropdownOpen,
      })}
      onClick={(e) => {
        e.stopPropagation()
        setIsDropdownOpen(true)
      }}
    >
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger>
          <EllipsisVertical className="my-2 ml-3 mr-1 h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={`w-48`}>
          {isDropdownOpen && (
            <>
              {transfer && (
                <TransferDropdownMenuItems assetTransfer={transfer} />
              )}
              {transaction && (
                <TransactionDropdownMenuItems transaction={transaction} />
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
